import React, { useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import {
  Users, UserCheck, UserX, UserPlus, Search, LayoutGrid, List,
  Mail, Phone, Shield, Edit2, Trash2, Power, Circle, MoreVertical, Filter,
    AtSign, Check, AlertCircle, Key, Eye, EyeOff, Copy, Building2, Fingerprint, BadgeCheck
} from 'lucide-react';
import { portalUsers } from '../../data/mockData';
import { userApi } from '../../api/userApi';
import { isApiConfigured } from '../../api/axiosClient';
import { LoadingSpinner, ErrorCard } from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { CardsPagination } from '../../components/CardsPagination';
import { useConfirm } from '../../components/ConfirmDialog';
import { ValidatedInput } from '../../components/ValidatedInput';
import { validators } from '../../utils/validators';
import { useAuth } from '../../auth/AuthContext';
import { process } from "@progress/kendo-data-query";
import { formatRelative } from '../../utils/loginTracker';


/* ── Role → colour ──────────────────────────────────────── */
const ROLE_COLORS = {
    'Super Admin': { bg: '#FEE2E2', color: '#DC2626', ring: 'rgba(220,38,38,0.15)' },
    Admin: { bg: '#EDE9FE', color: '#7C3AED', ring: 'rgba(124,58,237,0.15)' },
    Provider: { bg: '#DBEAFE', color: '#2563EB', ring: 'rgba(37,99,235,0.15)' },
    Staff: { bg: '#D1FAE5', color: '#059669', ring: 'rgba(5,150,105,0.15)' },
    Viewer: { bg: '#FEF3C7', color: '#D97706', ring: 'rgba(217,119,6,0.15)' },
};


/* ── Status pill ────────────────────────────────────────── */
const StatusPill = ({ status, onClick }) => {
  const active = status === 'Active';
  return (
    <span
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px 4px 8px', borderRadius: 999,
        fontSize: 12, fontWeight: 700,
        background: active ? 'rgba(16,185,129,0.1)' : 'rgba(156,163,175,0.12)',
        color: active ? '#059669' : '#6B7280',
        border: `1px solid ${active ? 'rgba(16,185,129,0.25)' : 'rgba(156,163,175,0.25)'}`,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
      }}
      title={onClick ? 'Click to toggle status' : undefined}
    >
      <Circle
        size={8}
        strokeWidth={0}
        fill={active ? '#10B981' : '#9CA3AF'}
      />
      {status}
    </span>
  );
};

const contactRow = {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 13, color: '#6B7280',
};
/* ── Role badge ─────────────────────────────────────────── */
const RoleBadge = ({ role }) => {
  const rc = ROLE_COLORS[role] || { bg: '#F3F4F6', color: '#6B7280' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px', borderRadius: 999,
        fontSize: 11, fontWeight: 700,
        background: rc.bg, color: rc.color,
        letterSpacing: 0.3,
      }}
    >
      {role}
    </span>
  );
};

/* ── User Card (for card grid view) ─────────────────────── */
const UserCard = ({ providers, onEdit, onDelete, onToggleStatus, onPasswordChange }) => {
  const [menuOpen, setMenuOpen] = useState(false);
    const active = providers.status === 'Active';

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E8ECF0',
        borderRadius: 16,
        padding: 18,
        position: 'relative',
        transition: 'all 0.2s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        opacity: active ? 1 : 0.75,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = '#CBD5E1';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = '#E8ECF0';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Header: avatar + name + menu */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
              <Avatar firstName={providers.firstName} lastName={providers.lastName} role={providers.Provider_Type} size={48} />
        <div style={{ flex: 1, minWidth: 0 }}>
                  <div data-cy="CardProviderName" style={{
            fontSize: 15, fontWeight: 700, color: '#1A1D21',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
                      {providers.firstName} {providers.lastName}
          </div>
          {/*{user.userName && (*/}
          {/*  <div style={{*/}
          {/*    fontSize: 12, color: '#6B7280', fontFamily: 'monospace',*/}
          {/*    marginTop: 1, display: 'flex', alignItems: 'center', gap: 3,*/}
          {/*  }}>*/}
          {/*    <AtSign size={11} strokeWidth={2.5} />{user.userName}*/}
          {/*  </div>*/}
          {/*)}*/}
          <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <RoleBadge data-cy="CardProviderProviderType" role={providers.providerType} />
                      <StatusPill data-cy="CardProviderStatus" name="statusPillCard" status={providers.status} />
          </div>
        </div>

        {/* Kebab menu */}
        {/*<div style={{ position: 'relative' }}>*/}
        {/*  <button*/}
        {/*    onClick={() => setMenuOpen(!menuOpen)}*/}
        {/*    style={{*/}
        {/*      width: 32, height: 32, borderRadius: 8, border: 'none',*/}
        {/*      background: menuOpen ? '#F3F4F6' : 'transparent',*/}
        {/*      cursor: 'pointer', display: 'flex',*/}
        {/*      alignItems: 'center', justifyContent: 'center',*/}
        {/*      color: '#6B7280',*/}
        {/*    }}*/}
        {/*    onMouseEnter={e => { e.currentTarget.style.background = '#F3F4F6'; }}*/}
        {/*    onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.background = 'transparent'; }}*/}
        {/*    aria-label="More options"*/}
        {/*  >*/}
        {/*    <MoreVertical size={16} strokeWidth={2} />*/}
        {/*  </button>*/}
        {/*  {menuOpen && (*/}
        {/*    <>*/}
        {/*      <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setMenuOpen(false)} />*/}
        {/*      <div style={{*/}
        {/*        position: 'absolute', right: 0, top: 36, zIndex: 11,*/}
        {/*        background: '#fff', border: '1px solid #E8ECF0',*/}
        {/*        borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.1)',*/}
        {/*        minWidth: 180, padding: 6, overflow: 'hidden',*/}
        {/*      }}>*/}
        {/*        <MenuItem*/}
        {/*          icon={Key}*/}
        {/*          label="Change password"*/}
        {/*          color="#7C3AED"*/}
        {/*          onClick={() => { setMenuOpen(false); onPasswordChange(user); }}*/}
        {/*        />*/}
        {/*        <MenuItem icon={Edit2} label="Edit user" onClick={() => { setMenuOpen(false); onEdit(user); }} />*/}
        {/*        <MenuItem*/}
        {/*          icon={Power}*/}
        {/*          label={active ? 'Deactivate' : 'Activate'}*/}
        {/*          color={active ? '#D97706' : '#059669'}*/}
        {/*          onClick={() => { setMenuOpen(false); onToggleStatus(user.id); }}*/}
        {/*        />*/}
        {/*        <div style={{ height: 1, background: '#F1F5F9', margin: '4px 0' }} />*/}
        {/*        <MenuItem*/}
        {/*          icon={Trash2}*/}
        {/*          label="Delete user"*/}
        {/*          color="#DC2626"*/}
        {/*          onClick={() => { setMenuOpen(false); onDelete(user.id); }}*/}
        {/*        />*/}
        {/*      </div>*/}
        {/*    </>*/}
        {/*  )}*/}
        {/*</div>*/}
      </div>

      {/* Body: contact info */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        <div style={contactRow}>
          <Mail size={14} color="#9CA3AF" strokeWidth={2} />
                  <span data-cy="CardProviderEmail" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {providers.email || 'N/A'}
          </span>
        </div>
        <div style={contactRow}>
          <Phone size={14} color="#9CA3AF" strokeWidth={2} />
                  <span data-cy="CardProviderPhone">{providers.phone || 'N/A'}</span>
              </div>

        <div style={contactRow}>
                  <Building2 size={14} color="#9CA3AF" strokeWidth={2} />
                  <span data-cy="CardProviderOrgName">{providers.organization || 'N/A'}</span>
        </div>
      </div>

      {/* Footer: last login + primary actions */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', paddingTop: 12,
        borderTop: '1px solid #F1F5F9',
          }}>
              <div style={contactRow}>
                  <Fingerprint size={14} color="#9CA3AF" strokeWidth={2} />
                  <span data-cy="CardProviderTaxId">Tax ID : {providers.federalId || 'N/A'}</span>
              </div>
              <div style={contactRow}>
                  <BadgeCheck size={14} color="#9CA3AF" strokeWidth={2} />
                  <span data-cy="CardProviderNPI">NPI : {providers.npi || 'N/A'}</span>
              </div>
        {/*<div style={{ fontSize: 11, color: '#9CA3AF' }}>*/}
        {/*  {user.lastLogin ? `Last seen ${relativeDate(user.lastLogin)}` : 'Never signed in'}*/}
        {/*          */}{/*Last seen <strong style={{ color: '#6B7280' }}>{relativeDate(user.lastLogin)}</strong>     */}

        {/*          */}{/*{*/}
        {/*          */}{/*    user.lastLogin*/}
        {/*          */}{/*        ? `Last seen ${formatRelative(user.lastLogin)} ${new Date(*/}
        {/*          */}{/*            user.lastLogin*/}
        {/*          */}{/*        ).toLocaleString('en-US', {*/}
        {/*          */}{/*            month: 'short',*/}
        {/*          */}{/*            day: 'numeric',*/}
        {/*          */}{/*            hour: 'numeric',*/}
        {/*          */}{/*            minute: '2-digit',*/}
        {/*          */}{/*        })}`*/}
        {/*          */}{/*        : 'Never signed in'*/}
        {/*          */}{/*}*/}
        {/*</div>*/}
        {/*<div style={{ display: 'flex', gap: 6 }}>*/}
        {/*  <button*/}
        {/*    onClick={() => onToggleStatus(user.id)}*/}
        {/*    style={{*/}
        {/*      padding: '6px 12px', borderRadius: 8,*/}
        {/*      border: `1px solid ${active ? 'rgba(217,119,6,0.25)' : 'rgba(5,150,105,0.25)'}`,*/}
        {/*      background: active ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.08)',*/}
        {/*      color: active ? '#D97706' : '#059669',*/}
        {/*      fontSize: 12, fontWeight: 700, cursor: 'pointer',*/}
        {/*      display: 'inline-flex', alignItems: 'center', gap: 5,*/}
        {/*      fontFamily: 'inherit',*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    <Power size={12} strokeWidth={2.5} />*/}
        {/*    {active ? 'Deactivate' : 'Activate'}*/}
        {/*  </button>*/}
        {/*  <button*/}
        {/*    onClick={() => onEdit(user)}*/}
        {/*    style={{*/}
        {/*      padding: '6px 12px', borderRadius: 8,*/}
        {/*      border: '1px solid rgba(13,148,136,0.2)',*/}
        {/*      background: 'rgba(13,148,136,0.08)',*/}
        {/*      color: '#0D9488',*/}
        {/*      fontSize: 12, fontWeight: 700, cursor: 'pointer',*/}
        {/*      display: 'inline-flex', alignItems: 'center', gap: 5,*/}
        {/*      fontFamily: 'inherit',*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    <Edit2 size={12} strokeWidth={2.5} />*/}
        {/*    Edit*/}
        {/*  </button>*/}
        {/*</div>*/}
      </div>
    </div>
  );
};

const MenuItem = ({ icon: Icon, label, onClick, color = '#1A1D21' }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 10,
      width: '100%', padding: '9px 12px', borderRadius: 8,
      border: 'none', background: 'transparent',
      fontSize: 13, fontWeight: 600, color,
      cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFB'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
  >
    <Icon size={15} strokeWidth={2} />
    {label}
  </button>
);

/* ── Avatar component ───────────────────────────────────── */
const Avatar = ({ firstName = '', lastName = '', role, size = 44 }) => {
    const initials = `${firstName.replace(/^Dr\.\s*/, '').charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase() || '?';
    const rc = ROLE_COLORS[role] || { bg: '#F3F4F6', color: '#6B7280' };
    return (
        <div
            style={{
                width: size, height: size, borderRadius: size * 0.32,
                background: `linear-gradient(135deg, ${rc.color}22, ${rc.color}11)`,
                color: rc.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: size * 0.36, fontWeight: 700,
                border: `1.5px solid ${rc.ring || rc.bg}`,
                flexShrink: 0,
                letterSpacing: 0.3,
            }}
        >
            {initials}
        </div>
    );
};



const formatters = {
    /* Format phone as (XXX) XXX-XXXX */
    phone: (value) => {
        if (!value) return '';
        const digits = value.replace(/\D/g, '').slice(0, 10);
        if (digits.length < 4) return digits;
        if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
};
/* ──────────────────────────────────────────────────────────────────── */
/* Main component                                                      */
/* ──────────────────────────────────────────────────────────────────── */
const ProviderRosterPage = () => {
    const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  //const [users, setUsers] = useState([]);
  const [providers, setProviders] = useState([]);
    const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
    const [loading, setLoading] = useState(false);
    const [gridLoading, setGridLoading] = useState(false);
    const [error, setError] = useState(null);

    const [totalRecords, setTotalRecords] = useState(0);
    const [filteredTotalRecords, setFilteredTotalRecords] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const [inactiveCount, setInactiveCount] = useState(0);

    const [page, setPage] = useState({
        skip: 0,
        take: 10,
    });

    // Sorting state
    const [sort, setSort] = useState([]);




  
    const styles = {
        overlay: {
            position: "fixed",
            inset: 0, // replaces top/left/width/height
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
        },
        gridOverlay: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
        },
    };
  


    const getProviderRoster = async () => {
        setGridLoading(true);
        setError(null);

        try {
            const res = await userApi.getProviderRoster2({
                searchText,
                sortColumnName: sort?.[0]?.field || '',
                sortColumnDirection:
                    sort?.[0]?.dir?.toUpperCase() || 'ASC',
                startIndex: page.skip,
                pageSize: page.take,
                exportCsv: false,
            });

            const providersData = res?.data?.Providers || [];

            const formattedProviders = providersData.map((p) => ({
                id: p.Provider_ID,
                providerId: p.Provider_ID,

                firstName: p.First_Name || '',
                middleName: p.Middle_Name || '',
                lastName: p.Last_Name || '',
                suffix: p.Suffix || '',

                providerName: [
                    p.First_Name,
                    p.Last_Name
                ]
                    .filter((v) => v?.trim())
                    .join(' '),

                fullName: [
                    p.First_Name,
                    p.Middle_Name,
                    p.Last_Name,
                    p.Suffix
                ]
                    .filter((v) => v?.trim())
                    .join(' '),

                organization: p.Org_Name || '',

                address: [
                    p.Address,
                    p.Address2,
                    p.City,
                    p.State,
                    p.Zip_Code
                ]
                    .map((v) => v?.trim())
                    .filter(Boolean)
                    .join(', '),

                phone: formatters.phone(p.Phone_Number) || '',
                phoneRaw: p.Phone_Number || '',

                fax: formatters.phone(p.Fax_Number) || '',

                email: p.Email_Address || '',

                sex: p.Sex || '',
                practiceType: p.Practice_Type || '',
                providerType: p.Provider_Type || '',

                // Keep these as strings.
                // Do NOT use BigInt here.
                npi: p.Provider_NPI?.toString() || '',
                federalId: p.Federal_ID?.toString() || '',

                licenseNumber: p.License_Number || '',
                specialties: p.Specialties || '',
                languages: p.Languages || '',
                education: p.Education || '',

                homeAddress: p.Home_Address || '',
                homeCity: p.Home_City || '',
                homeState: p.Home_State || '',
                homeZipCode: p.Home_Zip_Code || '',
                homePhone: formatters.phone(p.Home_Phone) || '',

                acceptingPatients:
                    p.Accepting_Patients === true ? 'Yes' : 'No',

                status:
                    p.Active === true ? 'Active' : 'Inactive',

                tableName: p.TableName || '',

               // totalRecord: p.TotalRecord || 0
            }));

            setProviders(formattedProviders);

            //alert(res?.data?.TotalRecord);

            // TotalRecord comes from COUNT(*) OVER() in the SP
            setTotalRecords(
                res?.data?.TotalRecord
            );

            setActiveCount(
                res?.data?.Active
            );

            setInactiveCount(
                res?.data?.Inactive
            );

            setFilteredTotalRecords(
                providersData[0]?.TotalRecord
            );

        } catch (err) {
            setError(
                err?.message || 'Failed to load providers'
            );
        } finally {
            setGridLoading(false);
        }
    };


  /* Load from API if configured */
    useEffect(() => {
        if (!isApiConfigured()) {
            return;
        }

        getProviderRoster();
    }, [
        searchText,
        page.skip,
        page.take,
        sort
    ]);


    

  /* Derived ─ filtered list & stats */
  //const filteredProviders = useMemo(() => {
  //  const q = searchText.trim().toLowerCase();
  //  return providers.filter((u) => {
  //    if (q && !`${u.firstName} ${u.lastName} ${u.email} ${u.userName || ''}`.toLowerCase().includes(q)) return false;
      
  //    return true;
  //  });
  //}, [providers, searchText]);


    

    const stats = useMemo(() => ({
        total: totalRecords,
        active: activeCount,
        inactive: inactiveCount,
    }), [totalRecords, activeCount, inactiveCount]);


  /* Actions */
  const openAddDialog = () => {
    setEditingUser({ ...emptyUser, id: null });
    setRevealPassword(false);
    setShowDialog(true);
  };
  const openEditDialog = (user) => {
    setEditingUser({ ...user });
    setRevealPassword(false);
    setShowDialog(true);
  };
  const closeDialog = () => {
    setShowDialog(false);
    setEditingUser(null);
    setRevealPassword(false);
  };

  /* ── Admin password reset dialog ─────────────────── */
  const openPasswordChangeDialog = (user) => {
    setPasswordChangeUser(user);
    setPwdNew('');
    setPwdConfirm('');
    setPwdReveal(false);
  };
  const closePasswordChangeDialog = () => {
    setPasswordChangeUser(null);
    setPwdNew('');
    setPwdConfirm('');
    setPwdReveal(false);
  };
  const handleSavePasswordChange = async () => {
    if (!passwordChangeUser) return;
    const pwdError = validators.password(pwdNew);
    if (pwdError) {
      toast.error(pwdError, { title: 'Password does not meet requirements' });
      return;
    }
    if (pwdNew !== pwdConfirm) {
      toast.error('Passwords do not match. Please re-enter.', { title: 'Password mismatch' });
      return;
    }
    const displayName = `${passwordChangeUser.firstName} ${passwordChangeUser.lastName}`;
    if (isApiConfigured()) {
      setPwdSaving(true);
        try {
            //alert("Change PAssword hit");
            const res= await userApi.resetPassword({
                userId: passwordChangeUser.id,
                newPassword: pwdNew
            });

            if (res.success) {
                toast.success(
                    `${displayName}'s password has been reset.`,
                    { title: 'Password changed' }
                );
                closePasswordChangeDialog();
            }
            else {
                toast.error(err.message || 'Failed to change password', { title: 'Password change failed' });
            }
        
        
      } catch (err) {
        toast.error(err.message || 'Failed to change password', { title: 'Password change failed' });
      } finally { setPwdSaving(false); }
    } else {
      // Mock mode — don't persist the password anywhere, just acknowledge.
      
      closePasswordChangeDialog();
    }
  };



  /* ── Table cell renderers ──────────────────────────────── */
  const NameCell = (props) => (
    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar
          firstName={props.dataItem.firstName}
          lastName={props.dataItem.lastName}
                  role={props.dataItem.providerType}
          size={32}
        />
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{
            fontWeight: 600, color: '#1A1D21', fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {props.dataItem.firstName} {props.dataItem.lastName}
            {/*{props.dataItem.providerType && (*/}
            {/*  <span style={{*/}
            {/*    fontSize: 11, color: '#6B7280', fontFamily: 'monospace',*/}
            {/*    fontWeight: 500,*/}
            {/*  }}>*/}
            {/*        @{props.dataItem.providerType}*/}
            {/*  </span>*/}
            {/*)}*/}
          </span>
          <span style={{ fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {props.dataItem.providerType}
          </span>
        </div>
      </div>
    </td>
  );

    //PhoneCell

    const PhoneCell = (props) => (
        <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
            {props.dataItem.phone || '-'}
        </td>
    );
    const AddressCell = (props) => (
        <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
            {props.dataItem.address || '-'}
        </td>
    );
    const OrganizationCell = (props) => (
        <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
            {props.dataItem.organization || '-'}
        </td>
    );
    const FederalIdCell = (props) => (
        <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
            {props.dataItem.federalId || '-'}
        </td>
    );
    const NpiCell = (props) => (
        <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
            {props.dataItem.npi || '-'}
        </td>
    );
    

  

  const StatusCellT = (props) => (
    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
      <StatusPill
        status={props.dataItem.status || '-'}
        /*onClick={() => toggleStatus(props.dataItem)}*/
      />
    </td>
  );


  /*Grid */
    // Paging state
    

    // Page change handler
    //const handlePageChange = (event) => {
    //    setPage({
    //        skip: event.page.skip,
    //        take: event.page.take,
    //    });
    //};

    const handlePageChange = (event) => {
        const newPage = event.page || event;  //  fallback for CardsPagination

        setPage({
            skip: newPage.skip,
            take: newPage.take,
        });
    };

    // Sort change handler
    const handleSortChange = (event) => {
        setSort(event.sort || []);

        setPage((prev) => ({
            ...prev,
            skip: 0
        }));
    };


    /*Grid */

  /* ── Render ────────────────────────────────────────────── */
  return (
    <div className="main-content">
      {/* ── Page header ───────────────────────────────── */}
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Users size={22} color="#0D9488" strokeWidth={2} />
            <h1 style={{ margin: 0 }}>Provider Roster</h1>
          </div>
          <p>Providers</p>
        </div>
        
      </div>

      {/* ── Stat cards ────────────────────────────────── */}
          <div className="stat-cards"
              /*style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}*/
              style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '16px',
                  marginBottom: 20,
              }}
          >
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon teal"><Users size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Providers</div>
        </div>
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon green"><UserCheck size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.active}</div>
          <div className="stat-label">Active</div>
        </div>
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon orange"><UserX size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.inactive}</div>
          <div className="stat-label">Inactive</div>
        </div>
        {/*      <div className="stat-card" style={{*/}
        {/*          display: 'flex',*/}
        {/*          flexDirection: 'column',*/}
        {/*          alignItems: 'center',*/}
        {/*          justifyContent: 'center',*/}
        {/*          textAlign: 'center',*/}
        {/*      }}>*/}
        {/*  <div className="stat-icon danger"><Shield size={20} strokeWidth={2} /></div>*/}
        {/*  */}{/*<div className="stat-value">{stats.admins}</div>*/}
        {/*  <div className="stat-label">Admins</div>*/}
        {/*</div>*/}
      </div>

          <div style={{ position: "relative" }}>
              {gridLoading && (
                  <div style={styles.gridOverlay}>
                      <LoadingSpinner
                          data-cy="grid-loader"
                          message="Loading Providers..."
                      />
                  </div>
              )}
      {/* ── Toolbar: filters + view toggle ─────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div
          className="user-mgmt-toolbar"
          style={{
            display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
          }}
        >
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200 }}>
            <span style={{
              position: 'absolute', left: 14, top: '50%',
              transform: 'translateY(-50%)', color: '#9CA3AF', display: 'flex',
            }}>
              <Search size={16} />
            </span>
            <input
              type="search"
              placeholder="Search by provider name, organization, npi...."
              value={searchText}
                          onChange={(e) => {
                              setSearchText(e.target.value);

                              setPage((prev) => ({
                                  ...prev,
                                  skip: 0
                              }));
                          }}
              style={{
                width: '100%', padding: '10px 14px 10px 40px',
                borderRadius: 10, border: '1.5px solid #E8ECF0',
                fontSize: 14, fontFamily: 'inherit',
                background: '#F8FAFB', outline: 'none',
                transition: 'all 0.15s',
              }}
              onFocus={e => {
                e.target.style.borderColor = '#0D9488';
                e.target.style.background = '#fff';
              }}
              onBlur={e => {
                e.target.style.borderColor = '#E8ECF0';
                e.target.style.background = '#F8FAFB';
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Filter size={15} color="#9CA3AF" />
                      
            {/*<select*/}
            {/*    name="allStatusesSelect"*/}
            {/*  value={filterStatus}*/}
            {/*  onChange={(e) => setFilterStatus(e.target.value)}*/}
            {/*  style={selectStyle}*/}
            {/*>*/}
            {/*  <option value="All">All Statuses</option>*/}
            {/*  <option value="Active">Active only</option>*/}
            {/*  <option value="Inactive">Inactive only</option>*/}
            {/*</select>*/}
          </div>

          {/* View toggle */}
          <div style={{
            display: 'flex', background: '#F1F5F9', borderRadius: 10,
            padding: 3, gap: 2,
          }}>
            <button
              onClick={() => setViewMode('cards')}
              style={toggleBtnStyle(viewMode === 'cards')}
              title="Card view"
              aria-pressed={viewMode === 'cards'}
            >
              <LayoutGrid size={14} strokeWidth={2.5} />
              Cards
            </button>
            <button
              onClick={() => setViewMode('table')}
              style={toggleBtnStyle(viewMode === 'table')}
              title="Table view"
              aria-pressed={viewMode === 'table'}
            >
              <List size={14} strokeWidth={2.5} />
              Table
            </button>
          </div>
        </div>

        {(searchText) && (
          <div style={{
            marginTop: 12, paddingTop: 12, borderTop: '1px solid #F1F5F9',
            fontSize: 13, color: '#6B7280',
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
                          <span>Showing <strong style={{ color: '#1A1D21' }}>{providers.length}</strong> of {filteredTotalRecords} providers</span>
            <button
              onClick={() => { setSearchText(''); }}
              style={{
                background: 'none', border: 'none',
                color: '#0D9488', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', fontFamily: 'inherit', padding: 0,
              }}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* ── Body ──────────────────────────────────────── */}
          {/*{loading && <LoadingSpinner data-cy="loading-spinner" message="Loading Providers..." />}*/}
      {error && <ErrorCard message={error} onRetry={() => window.location.reload()} />}

      {!loading && !error && (
        providers.length === 0 ? (
          <EmptyState hasFilters={searchText } />
              ) : viewMode === 'cards' ? (
            <>
          <div
            style={{
              display: 'grid',
              // minmax(280px, 1fr) keeps a single card inside a 316px phone
              // viewport while still giving multi-col layouts on larger screens.
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 16,
            }}
          >
            
                          {/*{processedData.map(u => (*/}
                              {providers.map(u => (
                <UserCard
                    data-testid="provider-card"
                key={u.id}
                providers={u}
                onEdit={openEditDialog}
                //onDelete={handleDelete}
                  onDelete={() => handleDeleteCard(u)}
                //onToggleStatus={toggleStatus}
                  //  onToggleStatus={() => toggleStatus(u)}
                onPasswordChange={openPasswordChangeDialog}
              />
            ))}
                          
                          </div>
                          <CardsPagination skip={page.skip}
                              take={page.take}
                                  total={filteredTotalRecords}
                              onPageChange={handlePageChange}
                          />
               </>       

        ) : (
          <div style={{ overflow: 'hidden', borderRadius: 16 }}>
            <div className="grid-scroll-wrapper">
                                  <Grid
                                      scrollable="scrollable"
                    style={{ height: "500px" }}
                    data={providers}         //MUST use processed data
                    skip={page.skip}
                    take={page.take}
                                          total={filteredTotalRecords}        //  REQUIRED                    
                    pageable={{ pageSizes: [5, 10, 20] }}
                                      sortable={true}
                                      resizable={true}
                    sort={sort}
                    onPageChange={handlePageChange}
                    onSortChange={handleSortChange}
                >
                                          <GridColumn field="providerName" title="Provider" width="280px" cells={{ data: NameCell }} />
                                          <GridColumn field="phoneNumber" title="Phone" width="150px" cells={{ data: PhoneCell }} />
                                      <GridColumn field="phone" title="Phone" width="150px" hidden={true } />
                                      
                                      <GridColumn field="address" title="Address" width="150px" cells={{ data: AddressCell }} />
                                          <GridColumn field="orgName" title="Organization" width="150px" cells={{ data : OrganizationCell }} />
                                      <GridColumn field="federalId" title="Tax Id" width="120px" cells={{ data: FederalIdCell }} />
                                          <GridColumn field="providerNpi" title="NPI" width="120px" cells={{ data: NpiCell }} />
                                          <GridColumn field="active" title="Status" width="120px" cells={{ data: StatusCellT }} />
                
              </Grid>
            </div>
          </div>
        )
      )}

      </div>
    </div>
  );
};

/* ── Empty state ────────────────────────────────────────── */
const EmptyState = ({ hasFilters }) => (
  <div className="card" style={{ textAlign: 'center', padding: '56px 24px' }}>
    <div style={{
      width: 64, height: 64, borderRadius: 16,
      background: 'rgba(13,148,136,0.08)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 16,
    }}>
      {hasFilters
        ? <Search size={28} color="#0D9488" strokeWidth={2} />
        : <Users size={28} color="#0D9488" strokeWidth={2} />
      }
    </div>
    <div style={{ fontSize: 17, fontWeight: 700, color: '#1A1D21', marginBottom: 6 }}>
      {hasFilters ? 'No providers match your filters' : 'No providers yet'}
    </div>
    <div style={{ fontSize: 13, color: '#6B7280' }}>
      {hasFilters ? 'Try clearing the filters or adjusting the search.' : 'Add your first tax id to get started.'}
    </div>
  </div>
);

/* ── Inline style helpers ──────────────────────────────── */
const iconBtn = (color) => ({
  width: 30, height: 30, borderRadius: 8,
  border: `1px solid ${color}33`,
  background: `${color}0A`,
  color,
  cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: 0,
});

const selectStyle = {
  padding: '8px 12px', borderRadius: 10,
  border: '1.5px solid #E8ECF0',
  fontSize: 13, fontWeight: 600, color: '#374151',
  background: '#F8FAFB', cursor: 'pointer',
  fontFamily: 'inherit', outline: 'none',
};

const toggleBtnStyle = (active) => ({
  padding: '7px 12px', borderRadius: 8, border: 'none',
  background: active ? '#fff' : 'transparent',
  color: active ? '#0D9488' : '#6B7280',
  fontSize: 12, fontWeight: 700,
  cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', gap: 5,
  boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
  fontFamily: 'inherit',
  transition: 'all 0.15s',
});

export default ProviderRosterPage;
